import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Tag, StatCard } from "@/components/Layout";
import { approvalStatusTone } from "@/lib/statusTone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import FinanceEntryFields, { isFinanceEntryValid } from "@/components/FinanceEntryFields";
import { useAuth } from "@/context/AuthContext";
import { Plus, Check, X, Link as LinkIcon, FileText, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import BulkUploadDialog from "@/components/BulkUploadDialog";
import useBulkSelection from "@/hooks/useBulkSelection";
import usePermissions from "@/lib/usePermissions";
import VendorProcurement from "@/components/VendorProcurement";
import DriveLinkPreviewModal from "@/components/DriveLinkPreviewModal";

const TODAY = () => new Date().toISOString().slice(0, 10);
const MONTH = () => new Date().toISOString().slice(0, 7);

const EMPTY_FORM = {
  item: "",
  quantity: 1,
  estimated_cost: "",
  justification: "",
  // Shared Finance Entry field set (mirrors the schema)
  project_id: "",
  department: "",
  category: "",
  sub_category: "",
  cost_type: "",
  aop: "",
  vendor_id: "",
  vendor: "",
  payment_mode: "",
  source_id: "",
  drive_link: "",
  month: MONTH(),
  date: TODAY(),
  // PI attachment (kept from Phase 43)
  attachment_url: "",
  attachment_name: "",
  attachment_kind: "",
  // Feb 2026 — Reimbursements
  request_type: "purchase",
  receipt_url: "",
};

export default function Purchases() {
  const { user } = useAuth();
  const can = usePermissions();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null); // null = new request, else PR id

  const [docPreview, setDocPreview] = useState(null); // { url, title }

  const load = async () => {
    const r = await api.get("/purchase-requests");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  // Auto-open "New request" when a caller deep-links via ?new=1 or
  // ?new=reimbursement (used by the Home Command Center / topbar
  // "+Create" launcher and the Approvals inbox "Reimbursement" chip).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const newParam = searchParams.get("new");
    if (newParam === "1" || newParam === "reimbursement") {
      if (newParam === "reimbursement") {
        setVpTab("reimbursements");
        setForm((f) => ({ ...f, request_type: "reimbursement" }));
      }
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 114 — Vendor Procurement tab, deep-linkable via ?vptab=procurement
  // (used by the Approvals inbox "review this quote" link).
  const [vpTab, setVpTab] = useState(searchParams.get("vptab") === "procurement" ? "procurement" : "requests");

  // For amount, the PR uses estimated_cost. The validator expects `amount`.
  const validatorView = useMemo(() => ({ ...form, amount: Number(form.estimated_cost) }), [form]);

  const isFormValid = useMemo(() => {
    if (!form.item?.trim()) return false;
    if (Number(form.quantity) <= 0) return false;
    if (!form.justification?.trim()) return false;
    if (form.request_type === "reimbursement" && !form.receipt_url?.trim()) return false;
    return isFinanceEntryValid(validatorView, { role: user?.role });
  }, [form, validatorView, user?.role]);

  const onLinkChange = (v) => {
    setForm((f) => ({ ...f, attachment_url: v, attachment_name: v ? "Google Drive link" : "", attachment_kind: v ? "link" : "" }));
  };

  const patchForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const create = async () => {
    if (!isFormValid) return;
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        estimated_cost: Number(form.estimated_cost),
      };
      if (editingId) {
        await api.patch(`/purchase-requests/${editingId}`, payload);
        toast.success("Request updated");
      } else {
        await api.post("/purchase-requests", payload);
        toast.success("Request submitted");
      }
      setOpen(false);
      setEditingId(null);
      setForm({ ...EMPTY_FORM, month: MONTH(), date: TODAY() });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startEdit = (r) => {
    setForm({
      item: r.item || "",
      quantity: r.quantity || 1,
      estimated_cost: r.estimated_cost ?? "",
      justification: r.justification || "",
      project_id: r.project_id || "",
      department: r.department || "",
      category: r.category || "",
      sub_category: r.sub_category || "",
      cost_type: r.cost_type || "",
      aop: r.aop || "",
      vendor_id: r.vendor_id || "",
      vendor: r.vendor || "",
      payment_mode: r.payment_mode || "",
      source_id: r.source_id || "",
      drive_link: r.drive_link || "",
      month: r.month || MONTH(),
      date: r.date || TODAY(),
      attachment_url: r.attachment_url || "",
      attachment_name: r.attachment_name || "",
      attachment_kind: r.attachment_kind || "",
      request_type: r.request_type || "purchase",
      receipt_url: r.receipt_url || "",
    });
    setEditingId(r.id);
    setOpen(true);
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete this purchase request for "${r.item}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/purchase-requests/${r.id}`);
      toast.success("Request deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const review = async (id, status) => {
    try { await api.patch(`/purchase-requests/${id}`, { status }); load(); toast.success(`Request ${status}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const canEdit = can("purchases_edit");
  const canDelete = canEdit;

  // Feb 2026 — Reimbursements share the same collection/API but get their
  // own tab, table and stat cards, filtered client-side by request_type.
  const filteredRows = useMemo(
    () => rows.filter((r) => (r.request_type === "reimbursement") === (vpTab === "reimbursements")),
    [rows, vpTab]
  );
  const filteredSummary = useMemo(() => {
    const byStatus = {};
    let total = 0;
    for (const r of filteredRows) {
      total += Number(r.estimated_cost || 0);
      byStatus[r.status] = (byStatus[r.status] || 0) + 1;
    }
    return { total_amount: total, count: filteredRows.length, by_status: byStatus };
  }, [filteredRows]);

  const bulk = useBulkSelection({
    collection: "purchase_requests",
    ids: filteredRows.map((r) => r.id),
    onDone: load,
  });

  const openAttachment = (r) => {
    if (!r.attachment_url) return;
    const url = r.attachment_url.startsWith("/api/files/")
      ? `${process.env.REACT_APP_BACKEND_URL}${r.attachment_url}`
      : r.attachment_url;
    window.open(url, "_blank", "noreferrer");
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto fade-in" data-testid="purchases-page">
      <div className="flex items-center gap-1 mb-6 border-b border-slate-200" data-testid="purchases-tabs">
        {[
          { id: "requests",       label: "Purchase requests" },
          { id: "reimbursements", label: "Reimbursements" },
          { id: "procurement",    label: "Vendor procurement" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setVpTab(t.id)}
            data-testid={`purchases-tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              vpTab === t.id ? "border-[#F54800] text-[#F54800]" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {vpTab === "procurement" ? (
        <VendorProcurement />
      ) : (
      <>
      <PageHeader
        eyebrow="Procurement"
        title={vpTab === "reimbursements" ? "Reimbursements" : "Purchase requests"}
        subtitle={vpTab === "reimbursements"
          ? "Request → Founder approval → Expense ledger. Every reimbursement needs a Google Drive receipt link at submission."
          : "Project, Department, Category, Sub-category, AOP, Vendor, Source — every field flows into Finance unchanged."}
        actions={
          <div className="inline-flex items-center gap-2 flex-wrap justify-end">
            <BulkUploadDialog section="purchase_requests" label="Purchase requests" onCommitted={load} />
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm({ ...EMPTY_FORM, month: MONTH(), date: TODAY() }); setEditingId(null); } }}>
              <DialogTrigger asChild>
                <Button
                  data-testid="new-pr-btn"
                  className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white"
                  onClick={() => setForm((f) => ({ ...f, request_type: vpTab === "reimbursements" ? "reimbursement" : "purchase" }))}
                >
                  <Plus size={14} className="mr-1.5" />{vpTab === "reimbursements" ? "New reimbursement" : "New request"}
                </Button>
              </DialogTrigger>

              <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pr-dialog">
                <DialogHeader><DialogTitle className="font-display">{editingId ? `Edit ${form.request_type === "reimbursement" ? "reimbursement" : "purchase request"}` : (form.request_type === "reimbursement" ? "New reimbursement" : "New purchase request")}</DialogTitle></DialogHeader>

                <div className="space-y-4 mt-2">
                  {/* Item details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <Label className="label-overline">Item *</Label>
                      <Input className="rounded-sm mt-1" data-testid="pr-item" value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} />
                    </div>
                    <div>
                      <Label className="label-overline">Qty *</Label>
                      <Input type="number" min="1" className="rounded-sm mt-1" data-testid="pr-qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="label-overline">{form.request_type === "reimbursement" ? "Amount spent ₹ *" : "Estimated cost ₹ *"}</Label>
                      <Input type="number" min="0" className="rounded-sm mt-1" data-testid="pr-cost" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} />
                    </div>
                    <div>
                      <Label className="label-overline">Date *</Label>
                      <Input type="date" className="rounded-sm mt-1" data-testid="pr-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                    </div>
                  </div>

                  {/* Shared Finance Entry fields */}
                  <div className="pt-3 border-t border-slate-200">
                    <FinanceEntryFields value={form} onChange={patchForm} testidPrefix="pr" />
                  </div>

                  <div>
                    <Label className="label-overline">Justification *</Label>
                    <Textarea rows={2} className="rounded-sm mt-1" data-testid="pr-justification" value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} />
                  </div>

                  {form.request_type === "reimbursement" ? (
                    <div className="border border-emerald-200 rounded-sm p-3 bg-emerald-50/40">
                      <Label className="label-overline flex items-center gap-1"><LinkIcon size={11} /> Google Drive receipt link <span className="text-red-500">*</span></Label>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <Input className="rounded-sm" placeholder="https://drive.google.com/file/d/…" value={form.receipt_url} onChange={(e) => setForm({ ...form, receipt_url: e.target.value })} data-testid="pr-receipt-input" />
                        {form.receipt_url && (
                          <button type="button" onClick={() => setDocPreview({ url: form.receipt_url, title: "Reimbursement receipt" })} className="shrink-0 text-slate-400 hover:text-[#F54800]" data-testid="pr-receipt-preview"><Eye size={15} /></button>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">Required — paste a link to the receipt/invoice proving this was already paid for out-of-pocket.</div>
                    </div>
                  ) : (
                    /* PI attachment — Google Drive link only */
                    <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/60">
                      <Label className="label-overline">Invoice / PI — Google Drive link (optional)</Label>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <Input className="rounded-sm" placeholder="https://drive.google.com/file/d/…" value={form.attachment_url} onChange={(e) => onLinkChange(e.target.value)} data-testid="pr-link-input" />
                        {form.attachment_url && (
                          <button type="button" onClick={() => setDocPreview({ url: form.attachment_url, title: "Purchase request attachment" })} className="shrink-0 text-slate-400 hover:text-[#F54800]" data-testid="pr-drive-link-preview"><Eye size={15} /></button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button onClick={create} disabled={!isFormValid} data-testid="submit-pr"
                    className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed">
                    {editingId ? "Save changes" : "Submit"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total spend" value={`₹ ${Number(filteredSummary.total_amount || 0).toLocaleString("en-IN")}`} hint={`${filteredSummary.count || 0} ${vpTab === "reimbursements" ? "reimbursements" : "requests"}`} />
        <StatCard label="Pending" value={filteredSummary.by_status?.pending || 0} accent="text-orange-700" />
        <StatCard label="Approved" value={filteredSummary.by_status?.approved || 0} accent="text-green-700" />
        <StatCard label="Rejected" value={filteredSummary.by_status?.rejected || 0} accent="text-red-700" />
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm" data-testid="pr-table">
          <thead className="bg-slate-50">
            <tr>
              {bulk.canBulk && (
                <th className="w-8 px-2 py-2">
                  <bulk.HeaderCheckbox />
                </th>
              )}
              {["By","Item","Dept","Cat","Sub","Cost Type","AOP","Vendor","Qty","Cost",vpTab === "reimbursements" ? "Receipt" : "Attachment","Status",""].map((h) => (
                <th key={h} className="label-overline px-3 py-2 text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredRows.map((r) => (
              <tr key={r.id} className={`hover:bg-slate-50 ${bulk.isSelected(r.id) ? "bg-orange-50/40" : ""}`} data-testid={`pr-row-${r.id}`}>
                {bulk.canBulk && (
                  <td className="w-8 px-2 py-2">
                    <bulk.RowCheckbox id={r.id} />
                  </td>
                )}
                <td className="px-3 py-2 text-xs text-slate-800 whitespace-nowrap">{r.user_name}</td>
                <td className="px-3 py-2 text-xs truncate max-w-[160px]" title={r.item}>{r.item}</td>
                <td className="px-3 py-2 text-xs">{r.department || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.category || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.sub_category || "—"}</td>
                <td className="px-3 py-2 text-xs font-mono">{r.cost_type || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.aop || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.vendor || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.quantity}</td>
                <td className="px-3 py-2 text-xs font-mono text-right whitespace-nowrap">₹ {Number(r.estimated_cost).toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-xs">
                  {r.request_type === "reimbursement" ? (
                    (r.receipt_url || r.pre_approval_url) ? (
                      <button onClick={() => setDocPreview({ url: r.receipt_url || r.pre_approval_url, title: "Reimbursement receipt" })} className="inline-flex items-center gap-1 text-emerald-700 hover:underline truncate max-w-[120px]" data-testid={`pr-receipt-${r.id}`}>
                        <LinkIcon size={11} /><span className="truncate">receipt</span>
                      </button>
                    ) : "—"
                  ) : (
                    r.attachment_url ? (
                      <button onClick={() => openAttachment(r)} className="inline-flex items-center gap-1 text-[#F54800] hover:underline truncate max-w-[120px]" data-testid={`pr-attach-${r.id}`}>
                        {r.attachment_kind === "pdf" ? <FileText size={11} /> : <LinkIcon size={11} />}<span className="truncate">{r.attachment_name || "open"}</span>
                      </button>
                    ) : "—"
                  )}
                </td>
                <td className="px-3 py-2"><Tag tone={approvalStatusTone(r.status)}>{r.status}</Tag></td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1 items-center">
                    {canEdit && (
                      <button onClick={() => startEdit(r)} className="p-1 rounded-sm border border-slate-200 hover:bg-slate-50" title="Edit" data-testid={`pr-edit-${r.id}`}><Pencil size={13} /></button>
                    )}
                    {canDelete && (
                      <button onClick={() => remove(r)} className="p-1 rounded-sm border border-slate-200 hover:bg-red-50 text-slate-400 hover:text-red-600" title="Delete" data-testid={`pr-delete-${r.id}`}><Trash2 size={13} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filteredRows.length === 0 && <tr><td colSpan={bulk.canBulk ? 14 : 13} className="px-5 py-6 text-center text-xs text-slate-400">{vpTab === "reimbursements" ? "No reimbursements yet." : "No requests yet."}</td></tr>}
          </tbody>
        </table>
      </div>
      <bulk.ActionBar />
      <bulk.ConfirmDialog />
      </>
      )}
      <DriveLinkPreviewModal open={!!docPreview} url={docPreview?.url} title={docPreview?.title} onClose={() => setDocPreview(null)} />
    </div>
  );
}
